/**
 * Canonical admin vocabularies. These are offline-fallback labels — the backend
 * serves the authoritative enum + `x-enum-labels` for every field at runtime via
 * `/admin/collection-schemas`. Keep them in sync with the Phase-1 docschema
 * (`backend/internal/docschema/schemas.go`).
 *
 * Y/DIRECTION (CMS plan D4) ships one true collection — Field Notes — whose only
 * closed enum is the article-body block `type`. A field note's `category` is a free
 * localized label (an {en,ar} leaf), not a queryable key, so it is NOT a vocabulary.
 */

export interface VocabOption {
  /** Stored canonical value. */
  value: string;
  /** i18n key for the human-facing label. */
  labelKey: string;
}

/**
 * Field-note article-body block types (`field_notes.doc.body[].type`). The closed
 * union the public renderer maps to known React elements — never raw HTML, so the
 * essay body is injection-safe by construction. Mirrors `fieldNoteDocSchema` (the
 * backend `block.type` enum); the localizer collapses the bilingual body to one
 * locale's block array before render.
 */
export const FIELD_NOTE_BLOCK_TYPES: VocabOption[] = [
  { value: 'paragraph', labelKey: 'vocab.block.paragraph' },
  { value: 'subheading', labelKey: 'vocab.block.subheading' },
  { value: 'list', labelKey: 'vocab.block.list' },
  { value: 'pullquote', labelKey: 'vocab.block.pullquote' },
  { value: 'aside', labelKey: 'vocab.block.aside' },
  { value: 'figure', labelKey: 'vocab.block.figure' },
];

/** Service offer layer (`service.doc.layer`) — the two-tier offer (core ⇄ add-on). */
export const SERVICE_LAYERS: VocabOption[] = [
  { value: 'core', labelKey: 'vocab.layer.core' },
  { value: 'addon', labelKey: 'vocab.layer.addon' },
];

/** Client / case-study type (`client.doc.type`, `caseStudy.doc.client_type`). */
export const CLIENT_TYPES: VocabOption[] = [
  { value: 'external', labelKey: 'vocab.clientType.external' },
  { value: 'group_brand', labelKey: 'vocab.clientType.groupBrand' },
];

/**
 * Team-member group (`team_member.doc.group`). The 2026 revision guide presents
 * the team in exactly two sections — Our Founders and Our Team; the per-
 * department breakdown and the open/hiring concept were retired. Mirrors
 * `groupChoice` in the backend docschema.
 */
export const TEAM_GROUPS: VocabOption[] = [
  { value: 'founders', labelKey: 'vocab.teamGroup.founders' },
  { value: 'team', labelKey: 'vocab.teamGroup.team' },
];

/** Insight (field-note) topic chip (`field_note.doc.topic`) — problem-led discovery filter. */
export const INSIGHT_TOPICS: VocabOption[] = [
  { value: 'strategy', labelKey: 'vocab.topic.strategy' },
  { value: 'branding', labelKey: 'vocab.topic.branding' },
  { value: 'social-paid', labelKey: 'vocab.topic.socialPaid' },
  { value: 'craft-ai', labelKey: 'vocab.topic.craftAi' },
];

/** Build the `x-enum-labels` map a JSON-schema enum field uses to show friendly text. */
export function enumLabels(options: VocabOption[]): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.value, o.labelKey]));
}

/** Build `{ value, label }` option list for a plain Select, resolving labels through t(). */
export function vocabSelectOptions(
  options: VocabOption[],
  t: (key: string) => string,
): { value: string; label: string }[] {
  return options.map((o) => ({ value: o.value, label: t(o.labelKey) }));
}
